// mulberry32, 32-bit integer only (§2.5). Never returns a float.

export type Rng = () => number;

/**
 * mulberry32. Returns a uint32 (`>>> 0`) — deliberately *not* the usual
 * `/ 4294967296` float variant, so track generation stays integer-exact.
 */
export function mulberry32(seed: number): Rng {
  let a = seed | 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t = (t ^ (t + Math.imul(t ^ (t >>> 7), t | 61))) | 0;
    return (t ^ (t >>> 14)) >>> 0;
  };
}

/** Uniform-ish integer in [0, n). Integer ops only; modulo bias is accepted. */
export function nextInt(rng: Rng, n: number): number {
  const m = n | 0;
  if (m <= 1) return 0;
  return (rng() % m) | 0;
}

/** Integer in [lo, hi] inclusive. */
export function nextRange(rng: Rng, lo: number, hi: number): number {
  const a = lo | 0;
  const b = hi | 0;
  if (b <= a) return a;
  return (a + nextInt(rng, b - a + 1)) | 0;
}

/** True with probability `permille`/1000. */
export function nextChance(rng: Rng, permille: number): boolean {
  return nextInt(rng, 1000) < (permille | 0);
}

// --- FNV-1a 32-bit ------------------------------------------------------------

export const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** Folds the 4 little-endian bytes of a 32-bit value into an FNV-1a hash. */
export function fnv1aInt32(hash: number, value: number): number {
  let h = hash >>> 0;
  const v = value | 0;
  for (let i = 0; i < 4; i++) {
    h = (h ^ ((v >>> (i * 8)) & 0xff)) >>> 0;
    h = Math.imul(h, FNV_PRIME) >>> 0;
  }
  return h >>> 0;
}

/** Lowercase, zero-padded 8-char hex. */
export function hashToHex(hash: number): string {
  return (hash >>> 0).toString(16).padStart(8, '0');
}
